import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useRazorpay from 'react-razorpay';
import { toast } from 'react-toastify';

import api from 'api/client';
import Loader from 'shared/common/loader';
import BuySellService from 'services/buy-sell.service';
import { userStore } from 'store/UserStore';

/**
 * Entry point for external/partner links that carry an already-created
 * Razorpay order:
 *   /partner-checkout?orderId=order_XXXXXXXXXXXXXX&paymentMethod=UPI|NEBK
 *
 * The link is untrusted input, so it is only ever used to pick WHICH order to
 * open and WHICH instrument to preselect. The amount is never read from the
 * URL: Razorpay takes it from the server-created order, and settlement is
 * confirmed by the backend (payment-update), not by anything the browser says.
 */
const RAZORPAY_ORDER_ID = /^order_[A-Za-z0-9]{14,20}$/;
export const PENDING_KEY = 'pendingPartnerCheckout';
const METHODS: Record<string, 'upi' | 'netbanking'> = {
  UPI: 'upi',
  NEBK: 'netbanking',
};

const PartnerCheckout = () => {
  const Razorpay = useRazorpay();
  const history = useHistory();
  const location = useLocation();
  const hasRun = useRef(false);
  const [error, setError] = useState('');
  const [hasHydrated, customerId, user] = userStore((s: any) => [
    s.hasHydrated,
    s.customerId,
    s.user,
  ]);

  useEffect(() => {
    if (hasRun.current || !hasHydrated) return;

    const params = new URLSearchParams(location.search);
    const orderId = params.get('orderId') || '';
    const paymentMethod = (params.get('paymentMethod') || '').toUpperCase();

    if (!RAZORPAY_ORDER_ID.test(orderId) || !METHODS[paymentMethod]) {
      hasRun.current = true;
      setError('This payment link is invalid.');
      return;
    }

    // Not fully signed in: every login path ends at /customers -> /dashboard
    // and drops returnUrl, so park this link and let /customers resume it
    // (see redirectCustomer). A token without a customerId (session not yet
    // resolved) must also go via /customers; /signin would bounce to
    // /dashboard because PublicRoute redirects any token holder.
    if (!api.getToken() || !customerId) {
      hasRun.current = true;
      localStorage.setItem(
        PENDING_KEY,
        JSON.stringify({
          url: location.pathname + location.search,
          at: Date.now(),
        })
      );
      history.replace(api.getToken() ? '/customers' : '/signin');
      return;
    }
    localStorage.removeItem(PENDING_KEY);

    // Guard against double-open (StrictMode / re-render) and refresh-replay:
    // an order is opened at most once per tab session.
    const key = `partnerCheckout:${orderId}`;
    if (sessionStorage.getItem(key)) {
      hasRun.current = true;
      history.replace('/passbook');
      return;
    }
    hasRun.current = true;
    sessionStorage.setItem(key, '1');

    const method = METHODS[paymentMethod];
    const rzp = new Razorpay({
      key: process.env.REACT_APP_RAZORPAY_KEY,
      order_id: orderId,
      currency: 'INR',
      name: 'eBullion',
      description: 'Buy Metals',
      image:
        'https://ebullion-dev.s3.ap-south-1.amazonaws.com/assets/eb-logo.png',
      async handler(response: any) {
        try {
          await BuySellService.checkPaymentStatus(
            { paymentId: response.razorpay_payment_id, orderId },
            new AbortController().signal
          );
        } catch (e) {
          // Backend webhook still settles the order; passbook shows final state.
        }
        history.replace('/passbook');
      },
      prefill: {
        name: user?.displayName,
        email: user?.email?.value,
        contact: user?.mobile?.value,
        method,
      },
      modal: {
        ondismiss: () => {
          sessionStorage.removeItem(key);
          toast.info('Payment cancelled.');
          history.replace('/dashboard');
        },
      },
      config: {
        display: {
          language: 'en',
          blocks: {
            banks: {
              name: method === 'upi' ? 'Pay via UPI' : 'Pay via Net Banking',
              instruments: [{ method }],
            },
          },
          sequence: ['block.banks'],
          preferences: { show_default_blocks: false },
        },
      },
      theme: { color: '#141034', backdrop_color: 'rgba(0, 0, 0, 0.90)' },
      timeout: 180,
    } as any);
    rzp.on('payment.failed', () => {
      sessionStorage.removeItem(key);
      toast.error('Payment failed. Please try again.');
    });
    rzp.open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHydrated, customerId]);

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <p>{error}</p>
        <button onClick={() => history.replace('/dashboard')}>Go to dashboard</button>
      </div>
    );
  }
  return <Loader />;
};

export default PartnerCheckout;
